import {
  SelectionState,
  SelectionStatus,
} from "@monolith-forensics/monolith-ui";

/*
  The selection bookkeeping behind ContactsList, kept apart from the component
  because all of it is pure: state in, state out, no React and no query. That is
  what makes it unit-testable, and the edge cases it exists for — a deselect
  landing back on "none", a blanket selection outliving the search it was made
  against, an inclusion set the List has mistaken for a select-all — are the
  ones that are painful to catch by hand.
*/

export const EMPTY_SELECTION: SelectionState = {
  selectedRowKeys: [],
  excludedRowKeys: [],
  selectionStatus: SelectionStatus.None,
  totalSelected: 0,
};

/**
 * Removes one key from a selection, honouring the List's inclusion/exclusion
 * model: while "all" is active the removal is recorded as an exclusion rather
 * than enumerating the keys that stay selected.
 *
 * `emptiesSelection` says whether this removal takes the *resolved* selection —
 * the contacts the caller can see and submit — down to nothing. Exclusion mode
 * cannot work that out from its own state: exclusions are only ever recorded
 * for rows the user has in front of them, so counting them against a
 * server-wide total never reaches it, and a selection emptied one row at a time
 * would sit in SomeExcluded with nothing resolved — pre-selecting every page
 * that scrolls in afterwards.
 */
export const deselectKey = (
  state: SelectionState,
  key: string,
  totalSelectable: number,
  emptiesSelection: boolean
): SelectionState => {
  const inExclusionMode =
    state.selectionStatus === SelectionStatus.All ||
    state.selectionStatus === SelectionStatus.SomeExcluded;

  if (inExclusionMode) {
    if (state.excludedRowKeys.includes(key)) return state;

    // Landing back on "none" drops the exclusions — they no longer describe
    // anything, and leaving them behind would corrupt the next select-all.
    if (emptiesSelection) return EMPTY_SELECTION;

    const excludedRowKeys = [...state.excludedRowKeys, key];

    return {
      selectedRowKeys: [],
      excludedRowKeys,
      selectionStatus: SelectionStatus.SomeExcluded,
      totalSelected: Math.max(totalSelectable - excludedRowKeys.length, 0),
    };
  }

  const selectedRowKeys = state.selectedRowKeys.filter((k) => k !== key);
  if (selectedRowKeys.length === state.selectedRowKeys.length) return state;

  // Keys outlive what they point at — a refetch flagging a contact as already
  // assigned drops it from the resolved selection while its key stays here — so
  // the resolved set decides "none" here too, not the key count alone.
  if (emptiesSelection || selectedRowKeys.length === 0) return EMPTY_SELECTION;

  return {
    selectedRowKeys,
    excludedRowKeys: [],
    selectionStatus: SelectionStatus.SomeIncluded,
    totalSelected: selectedRowKeys.length,
  };
};

/**
 * Collapses a blanket selection down to the concrete keys it currently resolves
 * to, leaving every other state alone.
 *
 * Exclusion mode means "whatever the query returns, less these", so the query
 * defines it: change the search and the same state silently comes to name a
 * different set of contacts — the picks made before it drop out and the new
 * matches are ticked in their place, neither of which the user asked for.
 * Pinning the resolved selection to keys before the query moves is what makes
 * it survive, because SomeIncluded names contacts rather than describing them
 * and `contactIndex` still resolves the ones the new page set has dropped.
 */
export const materializeSelection = (
  state: SelectionState,
  resolvedKeys: string[]
): SelectionState => {
  const inExclusionMode =
    state.selectionStatus === SelectionStatus.All ||
    state.selectionStatus === SelectionStatus.SomeExcluded;

  if (!inExclusionMode) return state;

  // Nothing resolved means nothing to pin — and an exclusion set describing an
  // empty selection is exactly what deselectKey refuses to leave behind.
  if (resolvedKeys.length === 0) return EMPTY_SELECTION;

  return {
    selectedRowKeys: resolvedKeys,
    excludedRowKeys: [],
    selectionStatus: SelectionStatus.SomeIncluded,
    totalSelected: resolvedKeys.length,
  };
};

/**
 * Puts back the SomeIncluded a count comparison has just talked the List out
 * of, leaving a real select-all alone.
 *
 * `applySelection` calls an inclusion set "all" as soon as its key count
 * reaches `totalRecords`, and totalRecords is scoped to the current search — so
 * three picks made with no search, plus one made inside a search matching two
 * contacts, arrives here as a blanket "all" over those two. The keys give it
 * away: Ctrl/Cmd+A records exactly the loaded rows, so a key pointing outside
 * them is arithmetic rather than intent, and honouring it would throw away the
 * picks that key is there to hold.
 */
export const demoteFalseSelectAll = (
  state: SelectionState,
  loadedKeys: Set<string>
): SelectionState => {
  if (state.selectionStatus !== SelectionStatus.All) return state;

  const spansUnloadedKeys = state.selectedRowKeys.some(
    (key) => !loadedKeys.has(key)
  );
  if (!spansUnloadedKeys) return state;

  return {
    selectedRowKeys: state.selectedRowKeys,
    excludedRowKeys: [],
    selectionStatus: SelectionStatus.SomeIncluded,
    totalSelected: state.selectedRowKeys.length,
  };
};
